// Slice 27 (ADR 0027): per-org notification bell.
//
// Polls GET /organizations/:orgId/notifications?limit=10 every 60s
// (foreground only — `refetchIntervalInBackground: false`) so a
// background-tab admin doesn't rack up calls. Clicking the bell opens
// an inline panel that lists the most recent notifications; clicking
// a row fires POST .../:id/read.
//
// Deliberately NOT in this slice: unread-only toggle inside the
// panel, infinite scroll, per-notification routing to the feedback
// inbox (slice 28 adds that when the admin feedback list ships),
// desktop toast notifications. If a slice-27 reviewer asks for any
// of these, split them into a follow-up — the goal here is a working
// badge count tied to the slice-27 outbox event.

import type { JSX } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../../api/client.js';
import type {
  FeedbackDetailResponse,
  MarkNotificationReadResponse,
  NotificationRow,
  NotificationsResponse,
} from '../../api/types.js';

interface Props {
  readonly orgId: string;
}

export function NotificationBell({ orgId }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const queryKey = ['notifications', orgId];
  const client = useQueryClient();
  const navigate = useNavigate();

  const notifications = useQuery({
    queryKey,
    queryFn: () =>
      apiFetch<NotificationsResponse>(`/organizations/${orgId}/notifications?limit=10`),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

  const markRead = useMutation({
    mutationFn: (notificationId: string) =>
      apiFetch<MarkNotificationReadResponse>(
        `/organizations/${orgId}/notifications/${notificationId}/read`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey });
    },
  });

  // Slice 28: click-through for feedback_received rows. Resolve the
  // outletId via the detail endpoint, mark the notification read, then
  // navigate to the inbox at that outlet with the feedback id in the
  // query string so the inbox scrolls / highlights it.
  const openFeedback = useMutation({
    mutationFn: async (row: NotificationRow): Promise<{ outletId: string; id: string }> => {
      if (row.resourceType !== 'feedback' || !row.resourceId) {
        throw new Error('not a feedback notification');
      }
      const detail = await apiFetch<FeedbackDetailResponse>(
        `/organizations/${orgId}/feedback/${row.resourceId}`,
      );
      // Fire-and-forget the mark-read; a failure here still lets the
      // user see the inbox row.
      try {
        await apiFetch<MarkNotificationReadResponse>(
          `/organizations/${orgId}/notifications/${row.id}/read`,
          { method: 'POST' },
        );
      } catch {
        // Ignore — the detail navigation is the user-visible action.
      }
      return { outletId: detail.feedback.outletId, id: detail.feedback.id };
    },
    onSuccess: ({ outletId, id }) => {
      setOpen(false);
      void client.invalidateQueries({ queryKey });
      navigate(`/orgs/${orgId}/outlets/${outletId}/feedback?id=${id}`);
    },
  });

  const unreadCount = notifications.data?.unreadCount ?? 0;
  const rows = notifications.data?.notifications ?? [];

  return (
    <div className="notification-bell">
      <button
        type="button"
        className="notification-bell-button"
        aria-label={`Notifications (${unreadCount} unread)`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden>🔔</span>
        {unreadCount > 0 ? (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        ) : null}
      </button>
      {open ? (
        <div className="notification-panel" role="dialog" aria-label="Notifications">
          {notifications.isLoading ? <p className="muted">Loading…</p> : null}
          {notifications.error ? (
            <p className="error">
              {notifications.error instanceof ApiError && notifications.error.status === 403
                ? 'You do not have access to notifications in this organization.'
                : 'Could not load notifications.'}
            </p>
          ) : null}
          {!notifications.isLoading && rows.length === 0 ? (
            <p className="muted">No notifications yet.</p>
          ) : null}
          {rows.length > 0 ? (
            <ul className="notification-list">
              {rows.map((n) => (
                <NotificationItem
                  key={n.id}
                  row={n}
                  onMarkRead={() => markRead.mutate(n.id)}
                  isMarking={markRead.isPending && markRead.variables === n.id}
                  onOpen={
                    n.resourceType === 'feedback' && n.resourceId
                      ? () => openFeedback.mutate(n)
                      : null
                  }
                  isOpening={openFeedback.isPending && openFeedback.variables?.id === n.id}
                />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

interface ItemProps {
  readonly row: NotificationRow;
  readonly onMarkRead: () => void;
  readonly isMarking: boolean;
  readonly onOpen: (() => void) | null;
  readonly isOpening: boolean;
}

function NotificationItem({
  row,
  onMarkRead,
  isMarking,
  onOpen,
  isOpening,
}: ItemProps): JSX.Element {
  const isUnread = row.readAt === null;
  return (
    <li className={`notification-row ${isUnread ? 'unread' : 'read'}`}>
      <div className="notification-row-main">
        {onOpen ? (
          <button type="button" className="notification-link" onClick={onOpen} disabled={isOpening}>
            {row.body}
          </button>
        ) : (
          <p className="notification-body">{row.body}</p>
        )}
        <p className="muted notification-meta">{formatRelative(row.createdAt)}</p>
      </div>
      {isUnread ? (
        <button
          type="button"
          className="button secondary small"
          onClick={onMarkRead}
          disabled={isMarking}
        >
          {isMarking ? '…' : 'Mark read'}
        </button>
      ) : null}
    </li>
  );
}

function formatRelative(iso: string): string {
  const diff = Date.now() - Date.parse(iso);
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)} h ago`;
  return new Date(iso).toLocaleDateString();
}
